"use client";
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaPlay, FaVolumeUp, FaExpand, FaHeart, FaShare, FaEye } from 'react-icons/fa';
import { IoMdClose } from 'react-icons/io';
import Image from 'next/image';
import VideoPlayer from '../videoplayer/VideoPlayer';
import VideoBand from './VideoBand';
import { SAMPLE_PROJECTS, SAMPLE_CLIENTS, type VideoProject, type Client } from '../../data/videoProjects';
import { useModal } from '@/app/context/ModalContext';

// -----------------------------
// HELPER FUNCTIONS
// -----------------------------

// Helper function to convert timestamp strings to seconds
function timeToSeconds(timeString: string): number {
  if (typeof timeString === 'string' && timeString.includes(':')) {
    const [hours, minutes, seconds] = timeString.split(':').map(Number);
    return (hours * 3600) + (minutes * 60) + seconds;
  }
  return typeof timeString === 'number' ? timeString : 0;
}

// -----------------------------
// MAIN COMPONENT
// -----------------------------
const ConversationPreview = (): React.ReactElement => {
  const { setIsModalOpen } = useModal();
  const [selectedProject, setSelectedProject] = useState<VideoProject | null>(null);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showResults, setShowResults] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleProjectClick = (project: VideoProject) => {
    setSelectedProject(project);
    setIsProjectModalOpen(true);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsProjectModalOpen(false);
    setSelectedProject(null);
    setIsModalOpen(false);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  const handleSearch = async () => {
    console.log('handleSearch called with query:', searchQuery);
    if (!searchQuery.trim()) {
      console.log('Empty query, returning');
      return;
    }
    
    console.log('Starting search process...');
    setIsSearching(true);
    setShowResults(false);
    
    try {
      console.log('Starting AI search for:', searchQuery);
      
      // Call the actual AI search API
      const response = await fetch('/api/conversation-preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          messages: [{ role: 'user', content: searchQuery }],
          usageCount: 0 // For now, assume free usage
        }),
      });

      console.log('API response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        throw new Error(`Search failed: ${response.status} ${response.statusText}`);
      }

      console.log('Starting to read streaming response...');
      
      // Handle streaming response
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      let searchData = '';
      const decoder = new TextDecoder();
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value, { stream: true });
        searchData += chunk;
        
        // Try to parse the accumulated data
        try {
          const lines = searchData.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') {
                console.log('Search completed');
                break;
              }
              
              try {
                const parsed = JSON.parse(data);
                if (parsed.type === 'searchResult') {
                  setSearchResults(prev => [...prev, parsed.data]);
                  setShowResults(true);
                }
              } catch (e) {
                console.log('Failed to parse JSON:', data);
              }
            }
          }
        } catch (e) {
          console.log('Failed to process chunk:', chunk);
        }
      }
      
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([{ error: 'Search failed. Please try again.' }]);
      setShowResults(true);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <section className="bg-black text-white relative overflow-hidden">
      <div className="relative z-10">
        {/* Video Band */}
        <VideoBand 
          videos={SAMPLE_PROJECTS}
          onVideoClick={handleProjectClick}
        />

        {/* Project Modal */}
        <AnimatePresence>
          {isProjectModalOpen && selectedProject && (
            <motion.div
              className="fixed inset-0 z-[9999] bg-black flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={closeModal}
            >
              <motion.div
                className="bg-black border-2 border-gray-600 max-w-4xl w-full max-h-[90vh] overflow-y-auto relative"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Close Button - Floating top right */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    closeModal();
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    closeModal();
                  }}
                  className="absolute top-4 right-4 z-[10000] w-12 h-12 flex items-center justify-center bg-black/90 backdrop-blur-sm hover:bg-white/20 text-white rounded-full transition-all duration-300 border border-gray-400 hover:border-white shadow-2xl cursor-pointer"
                  style={{ WebkitTapHighlightColor: 'transparent', pointerEvents: 'auto' }}
                  aria-label="Close"
                  type="button"
                >
                  <IoMdClose className="w-7 h-7" />
                </button>

                {/* Modal Header */}
                <div className="relative z-50 flex items-center justify-between p-6 border-b border-gray-700 pr-16 bg-black">
                  <div>
                    <h3 className="text-2xl font-bold text-white drop-shadow-lg">{selectedProject.title}</h3>
                    <p className="text-gray-300 drop-shadow-lg">{selectedProject.company}</p>
                  </div>
                </div>

                {/* Video Player Area */}
                <div className="aspect-video bg-gray-900">
                  <VideoPlayer
                    url={selectedProject.videoUrl}
                    start={0}
                    end={99999}
                    podcastName={selectedProject.title}
                    episodeName={selectedProject.company}
                    clipDuration={99999}
                    isFullEpisode={true}
                    onToggleFullEpisode={() => {
                      console.log('Toggle full episode');
                    }}
                  />
                </div>

              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default ConversationPreview;
